import React, { useState } from 'react';
import { Button, Empty, Flex, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { useSelector } from 'react-redux';
import { InfoCircleOutlined } from '@ant-design/icons';
import DateRangePicker, {
  filter_all,
  filter_last_month,
  filter_last_week,
  filter_this_month,
  filter_this_week,
  filter_this_year,
  filter_today,
  filter_yesterday,
  variant_dropdown,
  variant_radio_group,
} from '../../../components/filters/DateRangePicker';
import useScreenType from '../../../hooks/context/useScreenType';
import Overview from './overview';
import Google from './google';
import Customers from './customers';
import Orders from './orders';
import General from './general';
import OrderConversion from '../misc/order-conversion';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';
import useServiceProvider from '../../../hooks/data/useServiceProvider';
import MobileApp from './mobile-app/MobileApp';
import CompanySummaryModal from '../../../modals/company/CompanySummaryModal';
import useHelpers from '../../../hooks/context/useHelpers';
import useDateRangeFilter from '../../../hooks/utility/useDateRangeFilter';

export const tabs = {
  general: 'general',
  overview: 'overview',
  google: 'google',
  customers: 'customers',
  orders: 'orders',
  order_conversion: 'order_conversion',
  mobile_app: 'mobile_app',
};

export default function CompanyDashboard() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Dashboard:Company:CompanyDashboard' });

  const isMobileAppReleased = useSelector((state) => state.currentCompany?.company?.franchise?.mobile_app?.released);
  const companyId = useSelector((state) => state.currentCompany?.company?.id);
  const franchiseCompanies = useSelector((state) => state.currentFranchise?.franchise?.companies);
  // An owner can hold a franchise with no restaurant in it yet. Selecting it leaves no company
  // selected, and Shop refuses stats for a franchise without companies (403), which used to
  // render every tile as "NaN €" (Asana 1219404775591353).
  const isEmptyFranchise = !companyId && Array.isArray(franchiseCompanies) && franchiseCompanies.length === 0;

  const { isDesktop } = useScreenType();
  const { isAdmin, can } = usePermissions();
  // Self-serve (PLG) partners: Google and Orders-by-source tabs plus the key-info
  // summary are hidden (decided 2026-09-04) — data is empty/misleading pre-go-live.
  const { hasExternalAnalytics, hasPlgOnboarding } = useServiceProvider();
  const plgHiddenTabs = hasPlgOnboarding ? [tabs.google, tabs.orders] : [];
  const { getActiveTab, setActiveTab } = useHelpers();

  const [showKeyInfoModal, setShowKeyInfoModal] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useDateRangeFilter({
    period: filter_this_month,
  });

  const persistedTab = getActiveTab('company_dashboard.section', tabs.general);
  const activeTab = plgHiddenTabs.includes(persistedTab) ? tabs.general : persistedTab;
  const TabContent =
    {
      [tabs.general]: <General dateRangeFilter={dateRangeFilter} />,
      [tabs.overview]: <Overview dateRangeFilter={dateRangeFilter} />,
      [tabs.orders]: <Orders dateRangeFilter={dateRangeFilter} />,
      [tabs.google]: <Google dateRangeFilter={dateRangeFilter} />,
      [tabs.customers]: <Customers dateRangeFilter={dateRangeFilter} />,
      [tabs.order_conversion]: <OrderConversion dateRangeFilter={dateRangeFilter} />,
      [tabs.mobile_app]: <MobileApp dateRangeFilter={dateRangeFilter} />,
    }[activeTab] ?? null;

  const onChangeDateRangeFilter = (filter) => {
    if (filter.period) {
      setDateRangeFilter({ period: filter.period });

      return;
    }

    setDateRangeFilter({
      start_date: filter.start_date,
      end_date: filter.end_date,
    });
  };

  function renderDateRangeFilter() {
    return (
      <div className="mb-md">
        <DateRangePicker
          variant={isDesktop ? variant_radio_group : variant_dropdown}
          defaultFilter="filter_period_this_month"
          excludeFilters={[
            filter_today,
            filter_yesterday,
            filter_this_week,
            filter_last_week,
            filter_this_month,
            filter_last_month,
            filter_this_year,
            filter_all,
          ]}
          initialValue={dateRangeFilter}
          onChange={onChangeDateRangeFilter}
          customFilters={[
            {
              id: 'filter_period_today',
              name: t('date_range.today'),
              period: 'today',
            },
            {
              id: 'filter_period_yesterday',
              name: t('date_range.yesterday'),
              period: 'yesterday',
            },
            {
              id: 'filter_period_this_week',
              name: t('date_range.this_week'),
              period: 'this_week',
            },
            {
              id: 'filter_period_this_month',
              name: t('date_range.this_month'),
              period: 'this_month',
            },
            {
              id: 'filter_period_this_year',
              name: t('date_range.this_year'),
              period: 'this_year',
            },
            {
              id: 'filter_period_last_week',
              name: t('date_range.last_week'),
              period: 'last_week',
            },
            {
              id: 'filter_period_last_month',
              name: t('date_range.last_month'),
              period: 'last_month',
            },
            {
              id: 'filter_period_last_year',
              name: t('date_range.last_year'),
              period: 'last_year',
            },
            {
              id: 'filter_period_all',
              name: t('date_range.all'),
              start_date: null,
              end_date: null,
            },
          ]}
        />
      </div>
    );
  }

  if (isEmptyFranchise) {
    return (
      <>
        <h3 className="mb-lg">{t('title')}</h3>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('empty_franchise.description')}
          data-testid="dashboard-empty-franchise"
        />
      </>
    );
  }

  return (
    <>
      <Flex justify="space-between" className="mb-lg">
        <h3 className="my-auto">{t('title')}</h3>
        {!hasPlgOnboarding && can(ManagerPermission.ReadCompanySummary) && (
          <Button icon={<InfoCircleOutlined />} onClick={() => setShowKeyInfoModal(true)} className="my-auto">
            {t('buttons.view_key_info')}
          </Button>
        )}
      </Flex>
      {renderDateRangeFilter()}
      <Tabs
        activeKey={activeTab ?? tabs.general}
        onChange={(tab) => setActiveTab(tab, 'company_dashboard.section')}
        items={collect([
          {
            key: tabs.general,
            label: t(`tabs.${tabs.general}`),
          },
          {
            key: tabs.overview,
            label: t(`tabs.${tabs.overview}`),
          },
          {
            key: tabs.google,
            label: t(`tabs.${tabs.google}`),
          },
          {
            key: tabs.customers,
            label: t(`tabs.${tabs.customers}`),
          },
          {
            key: tabs.orders,
            label: t(`tabs.${tabs.orders}`),
          },
        ])
          .reject((item) => plgHiddenTabs.includes(item.key))
          .when(isAdmin && hasExternalAnalytics, (collection) =>
            collection.push({
              key: tabs.order_conversion,
              label: t(`tabs.${tabs.order_conversion}`),
            }),
          )
          .when(isMobileAppReleased, (collection) =>
            collection.push({
              key: tabs.mobile_app,
              label: t(`tabs.${tabs.mobile_app}`),
            }),
          )
          .toArray()}
      />
      {TabContent}
      {showKeyInfoModal && <CompanySummaryModal toggle={() => setShowKeyInfoModal(false)} />}
    </>
  );
}
